import {noop} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';

import {useAppDispatch, useAppSelector} from '../../src/helpers/reduxHelpers';
import {AppState} from '../../src/store/appState';
import {
  cancelNavigationAction,
  registerNavigationBlockerAction,
  unregisterNavigationBlockerAction,
} from '../../src/store/panels/app/appPanelActions';
import {generateNavigationBlockerId, NavigationBlockerId} from '../../src/store/panels/app/appPanelHelpers';
import {unblockNavigationAction} from '../../src/store/panels/app/appPanelRouteActions';
import {makeGetBlockedNavigation} from '../../src/store/panels/app/appPanelSelectors';
import {NewPanelId} from '../../src/store/panels/panelStateBase';

/*
 * Types.
 */

interface NavigationBlockingService {
  /** Whether navigation was attempted and blocked by us. */
  didBlockNavigation: boolean;
  /** Clears the navigation that was blocked. */
  cancelNavigation: () => void;
  /** Unblocks navigation and re-attempts the navigation that was blocked. */
  unblockNavigation: () => void;
}

/*
 * Hook.
 */

/**
 * Prevents the app from being navigated.
 *
 * @param panelId
 *   The ID of the originating panel to help look up the corresponding app panel.
 * @param shouldBlockNavigation
 *   Whether future navigation should be blocked.
 */
export function useNavigationBlocking(
  panelId: NewPanelId,
  shouldBlockNavigation: boolean,
  navigationBlockerId?: NavigationBlockerId,
): NavigationBlockingService {
  const dispatch = useAppDispatch();

  const getBlockedNavigation = useMemo(() => makeGetBlockedNavigation(), []);
  const blockedNavigation = useAppSelector((state: AppState) => getBlockedNavigation(state, {panelId}));
  const didBlockNavigation = shouldBlockNavigation && Boolean(blockedNavigation);

  // Register ourselves as a blocker.
  const generatedBlockerId = useMemo(() => generateNavigationBlockerId(), []);
  const blockerId = navigationBlockerId || generatedBlockerId;
  useEffect(() => {
    if (!shouldBlockNavigation) {
      return noop;
    }

    dispatch(registerNavigationBlockerAction(panelId, blockerId));
    return () => dispatch(unregisterNavigationBlockerAction(panelId, blockerId));
  }, [panelId, shouldBlockNavigation, dispatch, blockerId]);

  const cancelNavigation = useCallback(() => {
    // Clear the blocked navigation.
    dispatch(cancelNavigationAction(panelId));
  }, [panelId, dispatch]);

  const unblockNavigation = useCallback(() => {
    // Unregister ourselves as a blocker and re-attempt the blocked navigation.
    dispatch(unblockNavigationAction(panelId, blockerId));
  }, [panelId, dispatch, blockerId]);

  return {didBlockNavigation, cancelNavigation, unblockNavigation};
}
